import React, { useMemo, useState } from "react";
import { useAppDispatch } from "store";
import { Form, DateRangePicker, Input, HStack } from "rsuite";
import { TagFilter } from "@rsuite/icons";
import debounce from "lodash/debounce";

import FormControl from "components/base/FormControl";
import FormGroup from "components/base/FormGroup";
import Panel from "components/base/Panel";
import PrimaryButton from "components/Button/PrimaryButton";
import Icon from "components/base/Icon";

import { validationHasErrors } from "utils";
import { toBackendUTCString, parseUTCFromUrl, toDisplayTZ, fromDisplayTZ } from "utils/time";
import { CallFilters, callFormModel } from "types/filterConfig";

type FormModelType = typeof callFormModel;

type CallsFiltersProps = {
    expanded?: boolean;
    model: FormModelType;
    loading: boolean;
    filters: Record<string, any>;
    formError: Record<string, string>;
    setFilters: React.Dispatch<React.SetStateAction<CallFilters>>;
    setFormError: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    setRefreshKey: (key: number) => void;
    setSearchParams: (params: URLSearchParams) => void;
};


const CallsFilters: React.FC<CallsFiltersProps> = ({
    expanded,
    model,
    loading,
    filters,
    formError,
    setFilters,
    setFormError,
    setRefreshKey,
    setSearchParams
}) => {
    const [filtersState, setFiltersState] = useState(filters);

    const [timeRange, setTimeRange] = useState<{
        time_from: Date | null;
        time_to: Date | null;
    }>({
        time_from: parseUTCFromUrl(filters.time_from),
        time_to: parseUTCFromUrl(filters.time_to)
    });

    const filtersFormValue = useMemo(() => {
        return {
            time_range: timeRange.time_from && timeRange.time_to
                ? [timeRange.time_from, timeRange.time_to]
                : null,
            number_from: filters.number_from || "",
            number_to: filters.number_to || "",
            ip_src: filters.ip_src || "",
            ip_dst: filters.ip_dst || "",
            call_id: filters.call_id || "",
            method_code: filters.method_code || ""
        };
    }, [filters, timeRange]);

    const handleFilterChange = () => {
        const utcFrom = timeRange.time_from ? toBackendUTCString(timeRange.time_from) : "";
        const utcTo = timeRange.time_to ? toBackendUTCString(timeRange.time_to) : "";

        const nextUrlFilters: CallFilters = {
            number_from: filtersState.number_from || "",
            number_to: filtersState.number_to || "",
            ip_src: filtersState.ip_src || "",
            ip_dst: filtersState.ip_dst || "",
            // call_id: filtersState.call_id || "",
            // method_code: filtersState.method_code || "",
            time_from: utcFrom,
            time_to: utcTo
        };

        const validation = model.check(nextUrlFilters);
        const newErrors: Record<string, string> = {};

        Object.entries(validation).forEach(([key, val]: any) => {
            if (val.hasError) {
                newErrors[key] = val.errorMessage;
            }
        });

        setFormError(newErrors);

        if (validationHasErrors(validation)) return;

        const next = new URLSearchParams(window.location.search);
        Object.entries(nextUrlFilters).forEach(([k, v]) => next.set(k, v || ""));
        setSearchParams(next);

        setFilters(nextUrlFilters);
        setRefreshKey(Date.now());
    };

    const pickerValue =
        timeRange.time_from && timeRange.time_to
            ? [toDisplayTZ(timeRange.time_from)!, toDisplayTZ(timeRange.time_to)!]
            : null;


    return (
        // <FiltersContainer>
        <Panel
            // header="Filters"
            expanded={expanded}
            noStyles
        >
            <Form
                model={model}
                formError={formError}
                formDefaultValue={filtersFormValue}
                onChange={debounce((value: any) => {
                    setFiltersState({
                        number_from: value.number_from || "",
                        number_to: value.number_to || "",
                        ip_src: value.ip_src || "",
                        ip_dst: value.ip_dst || "",
                        call_id: value.call_id || "",
                        method_code: value.method_code || ""
                    });
                }, 500)}
            >
                <HStack spacing={16} justifyContent="space-between" alignItems="center">
                    <FormGroup gap={8} horizontal>
                        <FormControl
                            disabled={loading}

                            as={DateRangePicker}
                            width={330}
                            value={pickerValue}
                            onChange={(val: any) => {
                                const timeFrom: Date | null = val?.[0] ?? null;
                                const timeTo: Date | null = val?.[1] ?? null;

                                const utcFrom = fromDisplayTZ(timeFrom);
                                const utcTo = fromDisplayTZ(timeTo);

                                setTimeRange({ time_from: utcFrom, time_to: utcTo });
                            }}
                            onClean={() => {
                                setTimeRange({ time_from: null, time_to: null });
                            }}
                            showOneCalendar
                            size="md"
                            name="time_range"
                            placeholder="Select range"
                            format="dd/MM/yyyy HH:mm:ss"
                            cleanable={false}
                        />
                        <FormControl
                            disabled={loading}
                            width={180}
                            as={Input}
                            placeholder="Calling number..."
                            name="number_from"
                            size="md"
                        />
                        <FormControl
                            disabled={loading}
                            width={180}
                            as={Input}
                            placeholder="Called number..."
                            name="number_to"
                            size="md"
                        />
                        <FormControl
                            disabled={loading}
                            width={115}
                            as={Input}
                            placeholder="Incoming IP..."
                            name="ip_src"
                            size="md"
                        />
                        <FormControl
                            disabled={loading}
                            width={115}
                            as={Input}
                            placeholder="Outgoing IP..."
                            name="ip_dst"
                            size="md"
                        />
                    </FormGroup>
                    <PrimaryButton
                        disabled={loading}
                        onClick={() => {
                            handleFilterChange();
                        }}
                    >
                        <Icon as={TagFilter} />
                        <span>Show</span>
                    </PrimaryButton>
                </HStack>
            </Form>
        </Panel>
    );
};

export default CallsFilters; 